import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle2,
  FileSearch,
  Globe2,
  Layers3,
  MessageSquareQuote,
  Search,
  ShieldAlert,
  Sparkles
} from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";
type Source = { id: string; title: string; source: string; score: number };

const docs = [
  ["员工报销制度", "差旅、住宿、发票审批"],
  ["客户 SLA 服务等级", "首次响应、严重故障、合同优先"],
  ["权限申请流程", "生产权限、临时授权、自动回收"],
  ["销售折扣规则", "9 折、8 折、CFO 审批"],
  ["入职设备领取", "笔记本、门禁卡、特殊设备"]
];

const copy = {
  zh: {
    brand: "启序知库",
    lang: "EN",
    nav: ["知识库", "问答", "治理"],
    label: "独立案例站点 03 / 企业知识库 RAG",
    title: "一个会引用来源的企业知识库问答网站。",
    subtitle:
      "先检索企业制度、客户手册和流程文档，再让 MiniMax 基于命中文档生成答案，避免凭空回答。",
    primary: "开始提问",
    secondary: "查看文档",
    docTitle: "内置企业知识库",
    docSubtitle: "每个答案都必须来自可追溯的知识片段，未命中时直接提示转人工。",
    askTitle: "RAG 问答工作台",
    askSubtitle: "后端先召回相关文档，再直连 MiniMax 生成带来源的答案。",
    questionLabel: "问题",
    question: "企业版客户严重故障多久响应？如果合同里写了 7x24 应该以哪个为准？",
    run: "检索并回答",
    sample: "填入示例",
    waiting: "等待问题输入。",
    sources: "引用来源",
    confidence: "命中置信度",
    governanceTitle: "知识治理视图",
    governanceSubtitle: "真实 RAG 项目不能只看回答，还要看文档覆盖、权限边界和低置信度问题。",
    panels: [
      ["检索召回", "先做结构化片段召回，再把上下文交给模型回答。"],
      ["引用可追溯", "展示来源标题、文档位置和命中分数，方便业务复核。"],
      ["低置信转人工", "没有可靠来源时不编答案，转知识库维护或业务负责人。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 3 个真实独立网站"
  },
  en: {
    brand: "Qixu Knowledge",
    lang: "中文",
    nav: ["Docs", "Ask", "Govern"],
    label: "Standalone Case Site 03 / Enterprise RAG Q&A",
    title: "An enterprise knowledge site that cites its sources.",
    subtitle:
      "Retrieve policy, customer success and workflow documents first, then ask MiniMax to answer only from the matched context.",
    primary: "Ask now",
    secondary: "View docs",
    docTitle: "Built-in Knowledge Base",
    docSubtitle: "Every answer must be grounded in traceable snippets. Missing sources are escalated.",
    askTitle: "RAG Q&A Desk",
    askSubtitle: "The backend retrieves documents first, then calls MiniMax directly with grounded context.",
    questionLabel: "Question",
    question: "For enterprise customers, how fast do we respond to severe incidents? What if the contract says 7x24?",
    run: "Retrieve and answer",
    sample: "Use sample",
    waiting: "Waiting for a question.",
    sources: "Sources",
    confidence: "Confidence",
    governanceTitle: "Knowledge Governance",
    governanceSubtitle: "A real RAG project needs document coverage, permission boundaries and low-confidence handling.",
    panels: [
      ["Retrieval", "Retrieve structured snippets before sending context to the model."],
      ["Traceability", "Show source title, document location and match score for review."],
      ["Escalation", "When no reliable source exists, escalate instead of inventing an answer."]
    ],
    footer: "Qixu Technology AI case site · third real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [question, setQuestion] = useState(t.question);
  const [answer, setAnswer] = useState(t.waiting);
  const [sources, setSources] = useState<Source[]>([]);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setQuestion(copy[next].question);
    setAnswer(copy[next].waiting);
    setSources([]);
    setConfidence(null);
    setMeta("");
  }

  async function ask() {
    const value = question.trim();
    if (!value || loading) return;
    setLoading(true);
    setAnswer(locale === "zh" ? "正在检索知识库并生成答案..." : "Retrieving context and generating answer...");
    setSources([]);
    setConfidence(null);
    setMeta("");
    try {
      const response = await fetch("/api/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: value, locale })
      });
      const data = await response.json();
      setAnswer(data.text || t.waiting);
      setSources(data.sources || []);
      setConfidence(typeof data.confidence === "number" ? data.confidence : null);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setAnswer(locale === "zh" ? "网络异常，无法完成问答。" : "Network error. Q&A unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top">
          <span>知</span>
          <strong>{t.brand}</strong>
        </a>
        <nav>
          <a href="#docs">{t.nav[0]}</a>
          <a href="#ask">{t.nav[1]}</a>
          <a href="#governance">{t.nav[2]}</a>
        </nav>
        <button className="lang" onClick={switchLocale}>
          <Globe2 size={16} />
          {t.lang}
        </button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#ask">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#docs">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><Brain size={22} />RAG pipeline</div>
          <div className="pipeline">
            <div><Search size={18} /> Query rewrite</div>
            <div><FileSearch size={18} /> Retrieve snippets</div>
            <div><MessageSquareQuote size={18} /> Grounded answer</div>
            <div><ShieldAlert size={18} /> Escalate gaps</div>
          </div>
        </div>
      </section>

      <section className="docs" id="docs">
        <div className="section-copy">
          <h2>{t.docTitle}</h2>
          <p>{t.docSubtitle}</p>
        </div>
        <div className="doc-grid">
          {docs.map(([title, desc], index) => (
            <article key={title}>
              <BookOpen size={24} />
              <span>DOC-{String(index + 1).padStart(3, "0")}</span>
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ask" id="ask">
        <div className="desk">
          <div className="section-title">
            <Layers3 size={22} />
            <div>
              <h2>{t.askTitle}</h2>
              <p>{t.askSubtitle}</p>
            </div>
          </div>
          <label>
            {t.questionLabel}
            <textarea value={question} onChange={(event) => setQuestion(event.target.value)} />
          </label>
          <div className="composer-actions">
            <button onClick={() => setQuestion(t.question)}>{t.sample}</button>
            <button className="primary" onClick={ask} disabled={loading || !question.trim()}>
              {t.run}<Sparkles size={16} />
            </button>
          </div>
        </div>

        <div className="answer-card" aria-live="polite">
          <div className="answer-head">
            <CheckCircle2 size={20} />
            <strong>Grounded answer</strong>
          </div>
          <p>{answer}</p>
          {meta && <span className="meta">{meta}</span>}
          <div className="source-panel">
            <div>
              <strong>{t.confidence}</strong>
              <em>{confidence === null ? "--" : `${confidence}%`}</em>
            </div>
            <h3>{t.sources}</h3>
            {sources.length === 0 ? (
              <p className="muted">No source selected.</p>
            ) : (
              sources.map((item) => (
                <article key={item.id}>
                  <strong>{item.title}</strong>
                  <span>{item.source}</span>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="governance" id="governance">
        <div>
          <h2>{t.governanceTitle}</h2>
          <p>{t.governanceSubtitle}</p>
        </div>
        <div className="stats">
          <div><span>文档片段</span><strong>186</strong><em>已索引</em></div>
          <div><span>低置信问题</span><strong>14</strong><em>待补充</em></div>
          <div><span>引用覆盖</span><strong>92%</strong><em>可追溯</em></div>
        </div>
        <div className="govern-grid">
          {t.panels.map(([title, content]) => (
            <article key={title}>
              <ShieldAlert size={20} />
              <h3>{title}</h3>
              <p>{content}</p>
            </article>
          ))}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
